import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import { ContentScheduleModal } from "./ContentScheduleModal";
import { FullscreenVideoPlayer } from "../FullscreenVideoPlayer";
import { useAuthStore } from "@/stores/authStore";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useContentDetail } from "./content-detail/hooks/useContentDetail";
import { useInlineVideoPlayer } from "./content-detail/hooks/useInlineVideoPlayer";
import { useFeedback } from "./content-detail/hooks/useFeedback";
import { MobileLayout } from "./content-detail/MobileLayout";
import { DesktopLayout } from "./content-detail/DesktopLayout";
import type { ContentEvent, SocialConnection } from "./content-detail/types";

interface ContentDetailModalProps {
	event: ContentEvent | null;
	isOpen: boolean;
	onClose: () => void;
	connectedPlatforms?: SocialConnection[];
	onValidate?: () => void;
	onEventUpdate?: (eventId: string, patch: Partial<ContentEvent>) => void;
	variant?: "modal" | "inline";
	isAutoValidated?: boolean;
}

export function ContentDetailModal({
	event,
	isOpen,
	onClose,
	connectedPlatforms = [],
	onValidate,
	onEventUpdate,
	variant = "modal",
	isAutoValidated = false,
}: Readonly<ContentDetailModalProps>) {
	const { userId } = useAuthStore();
	const { isAdmin } = useIsAdmin();

	const detail = useContentDetail(event, {
		isOpen,
		userId,
		isAdmin,
		connectedPlatforms,
		onValidate,
		onClose,
		onEventUpdate,
	});

	const videoPlayer = useInlineVideoPlayer(
		detail.rawInlineVideoKeys,
		event?.id,
	);

	const feedback = useFeedback(
		event?.id,
		userId,
		videoPlayer.inlineActiveVersion,
	);

	if (!event) return null;

	// When the currently-playing version is disabled, switch to the first remaining enabled version.
	const handleToggleVersionEnabled = async (index: number) => {
		const isCurrentlyEnabled = detail.versionsEnabled[index];
		if (isCurrentlyEnabled && videoPlayer.inlineActiveVersion === index) {
			const firstEnabled = detail.versionsEnabled.findIndex(
				(v, i) => v && i !== index,
			);
			if (firstEnabled !== -1) {
				videoPlayer.setInlineActiveVersion(firstEnabled);
			}
		}
		await detail.toggleVersionEnabled(index);
	};

	const isInline = variant === "inline";

	// ---- Inline (desktop 3-panel) ----
	if (isInline) {
		if (!isOpen) return null;

		return (
			<DesktopLayout
				event={event}
				editingField={detail.editingField}
				setEditingField={detail.setEditingField}
				editTitle={detail.editTitle}
				editDescription={detail.editDescription}
				isReadOnly={detail.isReadOnly}
				isValidated={detail.isValidated}
				isValidating={detail.isValidating}
				allDatesUnchecked={detail.allDatesUnchecked}
				allItems={detail.allItems}
				checkedDates={detail.checkedDates}
				scheduleModalOpen={detail.scheduleModalOpen}
				setScheduleModalOpen={detail.setScheduleModalOpen}
				showCloseConfirm={detail.showCloseConfirm}
				setShowCloseConfirm={detail.setShowCloseConfirm}
				activeVideoUrl={detail.activeVideoUrl}
				effectivePlatforms={detail.effectivePlatforms}
				rawInlineVideoKeys={detail.rawInlineVideoKeys}
				versionsEnabled={detail.versionsEnabled}
				isAutoValidated={isAutoValidated}
				userProfile={detail.userProfile}
				onSaveTitle={detail.saveTitle}
				onSaveDescription={detail.saveDescription}
				onValidate={detail.handleValidate}
				onToggleDate={detail.toggleDateCheck}
				onSaveSchedule={detail.handleSaveSchedule}
				onValidateAndLaunch={detail.handleValidateAndLaunch}
				handleConfirmClose={detail.handleConfirmClose}
				handleCloseVideo={detail.handleCloseVideo}
				videoRef={videoPlayer.inlineVideoRef}
				inlineActiveUrl={videoPlayer.inlineActiveUrl}
				inlinePlaying={videoPlayer.inlinePlaying}
				setInlinePlaying={videoPlayer.setInlinePlaying}
				inlineMuted={videoPlayer.inlineMuted}
				inlineCurrentTime={videoPlayer.inlineCurrentTime}
				setInlineCurrentTime={videoPlayer.setInlineCurrentTime}
				inlineDuration={videoPlayer.inlineDuration}
				setInlineDuration={videoPlayer.setInlineDuration}
				inlineActiveVersion={videoPlayer.inlineActiveVersion}
				setInlineActiveVersion={videoPlayer.setInlineActiveVersion}
				onTogglePlay={videoPlayer.toggleInlinePlay}
				onToggleMute={videoPlayer.toggleInlineMute}
				onSeek={videoPlayer.handleSeekToInline}
				formatTime={videoPlayer.formatVideoTime}
				onToggleVersionEnabled={handleToggleVersionEnabled}
				feedbackComments={feedback.feedbackComments}
				isSendingFeedback={feedback.isSendingFeedback}
				focusCommentId={feedback.focusCommentId}
				activeMarkerId={feedback.activeMarkerId}
				showFeedbackPanel={feedback.showFeedbackPanel}
				setShowFeedbackPanel={feedback.setShowFeedbackPanel}
				setFocusCommentId={feedback.setFocusCommentId}
				setActiveMarkerId={feedback.setActiveMarkerId}
				onOpenFeedbackPanel={feedback.handleOpenFeedbackPanel}
				onSendFeedback={feedback.handleSendFeedbackFromPanel}
				onCommentsChange={feedback.handleFeedbackCommentsChange}
				onMarkerClick={(comment) => {
					videoPlayer.handleSeekToInline(comment.timestamp);
					feedback.setShowFeedbackPanel(true);
					feedback.setActiveMarkerId(comment.id);
					feedback.setFocusCommentId(comment.id);
					setTimeout(() => feedback.setFocusCommentId(null), 200);
				}}
			/>
		);
	}

	// ---- Modal (mobile/default) ----
	return (
		<>
			<Dialog
				open={isOpen && !detail.activeVideoUrl}
				onOpenChange={(open) => !open && detail.handleClose()}
			>
				<DialogContent className="bg-black/95 border-none text-white w-full h-full max-w-full max-h-full backdrop-blur-xl p-0 overflow-hidden m-0 pt-[env(safe-area-inset-top)] [&>button[data-slot='dialog-close']]:top-[calc(env(safe-area-inset-top)+1rem)]">
					<MobileLayout
						event={event}
						isInline={false}
						editingField={detail.editingField}
						setEditingField={detail.setEditingField}
						editTitle={detail.editTitle}
						editDescription={detail.editDescription}
						isValidated={detail.isValidated}
						isValidating={detail.isValidating}
						isPublishing={detail.isPublishing}
						showPublishMenu={detail.showPublishMenu}
						setShowPublishMenu={detail.setShowPublishMenu}
						globalCrosspostEnabled={detail.globalCrosspostEnabled}
						crosspostDisabled={detail.crosspostDisabled}
						allItems={detail.allItems}
						isReadOnly={detail.isReadOnly}
						hookVariations={detail.hookVariations}
						isAbTestingEvent={detail.isAbTestingEvent}
						versionsEnabled={detail.versionsEnabled}
						effectivePlatforms={detail.effectivePlatforms}
						videoCompatiblePlatforms={detail.videoCompatiblePlatforms}
						onClose={detail.handleClose}
						onSaveTitle={detail.saveTitle}
						onSaveDescription={detail.saveDescription}
						onValidate={detail.handleValidate}
						onCrossPost={detail.handleCrossPost}
						onCrosspostToggle={detail.handleCrosspostToggle}
						onToggleVersionEnabled={handleToggleVersionEnabled}
						onOpenVideo={detail.handleOpenVideo}
						onOpenSchedule={() => detail.setScheduleModalOpen(true)}
					/>
				</DialogContent>

				<ContentScheduleModal
					isOpen={detail.scheduleModalOpen}
					onClose={() => detail.setScheduleModalOpen(false)}
					contentTitle={event.title}
					publications={event.publicationTimes ?? []}
					connectedPlatforms={detail.effectivePlatforms}
					onSave={detail.handleSaveSchedule}
					onValidateAndLaunch={detail.handleValidateAndLaunch}
					isMultihook={event.isMultihook}
				/>
			</Dialog>

			{detail.activeVideoUrl && (
				<FullscreenVideoPlayer
					videoId={event.id}
					videoSrc={detail.activeVideoUrl}
					onClose={detail.handleCloseVideo}
					currentUserName={
						detail.userProfile
							? `${detail.userProfile.first_name ?? ""} ${detail.userProfile.last_name ?? ""}`.trim() ||
								"Utilisateur"
							: "Utilisateur"
					}
					currentUserAvatar={detail.userProfile?.avatar_url ?? undefined}
				/>
			)}

			<AlertDialog
				open={detail.showCloseConfirm}
				onOpenChange={detail.setShowCloseConfirm}
			>
				<AlertDialogContent className="bg-black/95 border-white/10 text-white">
					<AlertDialogHeader>
						<AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
						<AlertDialogDescription className="text-white/60">
							Vos modifications ne seront pas enregistrées. Êtes-vous sûr ?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">
							Annuler
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={detail.handleConfirmClose}
							className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
						>
							Fermer sans enregistrer
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
